import { detect } from "detect-browser";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/modules/theme/components/Button/Button";
import { DialogButtonsWrapper } from "@/modules/theme/components/Dialog/components/DialogButtonsWrapper";
import { Dialog } from "@/modules/theme/components/Dialog/Dialog";
import { ButtonLink } from "@/modules/theme/components/Link/components/ButtonLink/ButtonLink";

const translationNamespace = "common/DownloadAppFromStoreDialog";

const downloadAppModalShownKey = "downloadAppModalShown";

const playStoreLink =
  "https://play.google.com/store/apps/details?id=com.levelupoperations.app";
const appStoreLink =
  "https://apps.apple.com/pl/app/level-up-operations/id1637816451";

export const DownloadAppFromStoreDialog: React.FC = () => {
  const { t } = useTranslation(translationNamespace);
  const browser = detect();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const preventInstallPrompt = (e: Event) => {
      e.preventDefault();
    };
    window.addEventListener("beforeinstallprompt", preventInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", preventInstallPrompt);
    };
  }, []);

  useEffect(() => {
    const downloadAppModalShown = localStorage.getItem(
      downloadAppModalShownKey
    );
    const mobileOS = ["iOS", "Android OS", "android"];

    const isStandalone = window.matchMedia("(display-mode: standalone)")
      .matches;
    const isIOSWebView = browser?.name === "ios-webview";

    // iOS web view does not return `true` when asking about display-mode: standalone
    // so we have to check if the browser name equals to `ios-webview`
    if (!isIOSWebView && browser?.os && mobileOS.includes(browser.os)) {
      if (!isStandalone && !downloadAppModalShown) {
        setIsDialogOpen(true);
      }
    }
  }, [browser]);

  const closeModal = useCallback(() => {
    setIsDialogOpen(false);
    localStorage.setItem(downloadAppModalShownKey, "true");
  }, []);

  const isIOS = browser?.os === "iOS";

  return (
    <Dialog
      isOpen={isDialogOpen}
      onClose={() => setIsDialogOpen(false)}
      title={t("Download the app")}
      description={
        isIOS
          ? t("Do you want to download the app from the App Store?")
          : t("Do you want to download the app from the Play Store?")
      }
    >
      <DialogButtonsWrapper>
        <Button variant="secondary" onClick={closeModal}>
          {t("Maybe later")}
        </Button>
        <ButtonLink
          source="External"
          to={isIOS ? appStoreLink : playStoreLink}
          onClick={closeModal}
        >
          {t("Yes!")}
        </ButtonLink>
      </DialogButtonsWrapper>
    </Dialog>
  );
};
